import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { personalPost, personalPut, type PersonalRequestOptions } from '../../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson, sanitizeForTerminal } from '../../../lib/output';
import { PERSONAL_RESUMES_BASE, type UpdateResumeNameDto } from '@wport/core';

interface CopyFlags {
	name?: string;
}

/** 同 view.ts 慣例：檔案小而聚焦，各檔各自複製一份，不額外拉一個共用模組。 */
function requireEncId(encId: string): string {
	const trimmed = encId.trim();
	if (!trimmed) {
		throw new CliError('enc_id must not be empty', ExitCode.InvalidArgument);
	}
	return trimmed;
}

function printCopyResult(ctx: ResolvedContext, sourceEncId: string, newEncId: string): void {
	if (ctx.format === 'json') {
		printJson({ enc_id: newEncId });
		return;
	}
	process.stdout.write(`Copied resume ${sanitizeForTerminal(sourceEncId)} → new resume: ${sanitizeForTerminal(newEncId)}\n`);
}

/**
 * `resumes copy` 核心（R5，design doc §5.2）：`POST /resumes/{enc_id}/duplicate`（空 body，D8）
 * → 201 `{enc_id}`（server 命名「原名 - 複製」，不回 name）。422 `resume_limit_reached` 不特別
 * 攔截改寫訊息——`personalPost` 對非 2xx 已轉成 `ServerClientHttpError`（exit 3），原樣透傳即是
 * plan Task 15 規格要求的「透傳」，與 create 編排 `createShell` 的專屬引導訊息刻意不同。
 *
 * `--name` 給定 → 複製成功後續發 `PUT /resumes/{新enc_id}/name`（D8 兩步編排，同
 * resume-orchestrator.ts `createAggregate` 的 rename 步驟）。複製本身已成功時就先印出結果
 * （新 enc_id 上到 stdout，script 可讀）；rename 失敗不影響已存在的複製——新履歷還在，只是留著
 * 伺服器預設命名，故不回滾、只讓錯誤帶著新 enc_id 上拋（exit 3），不再另外印一次 Warning 重複
 * 同一件事（結果已經在 stdout，錯誤訊息只留在 stderr 這一個管道）。
 */
export async function runResumesCopy(ctx: ResolvedContext, encId: string, name: string | undefined): Promise<void> {
	const trimmed = requireEncId(encId);
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };

	const { body } = await personalPost(opts, `${PERSONAL_RESUMES_BASE}/${encodeURIComponent(trimmed)}/duplicate`, {}, { idempotencyKey: randomUUID() });
	const { enc_id: newEncId } = unwrapDataResponse<{ enc_id: string }>(body);

	printCopyResult(ctx, trimmed, newEncId);

	if (name === undefined) return;

	try {
		await personalPut(opts, `${PERSONAL_RESUMES_BASE}/${encodeURIComponent(newEncId)}/name`, { name } satisfies UpdateResumeNameDto, { idempotencyKey: randomUUID() });
	} catch (err) {
		const reason = err instanceof Error ? err.message : String(err);
		throw new CliError(
			`Resume ${trimmed} was copied to ${newEncId}, but renaming it to "${name}" failed: ${reason}. ` +
				'The copy exists under its server-assigned default name — this CLI has no standalone rename command in v1.',
			ExitCode.ServerClientError
		);
	}
}

export function registerPersonalResumesCopy(parent: Command): void {
	parent
		.command('copy <enc_id>')
		.description('Duplicate a resume (counts toward your resume limit)')
		.option('--name <name>', 'rename the new copy after duplicating')
		.action(async (encId: string, flags: CopyFlags, command: Command) => {
			await runResumesCopy(resolveContext(command), encId, flags.name);
		});
}
